import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observable, reaction, comparer } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useIntl } from 'react-intl';

import MessagePropType from '../definitions/MessagePropType';
import messageToString from '../functions/messageToString';
import { useNavigation } from './Navigation';


function NavigationPage({
    id,
    title,

    route,
    routeParams,

    onActivate,

    children,
}) {
    const state = useNavigationPageState({
        id,
        title,
        route,
        routeParams,
        onActivate,
    });

    // Bail if we're not the active page
    if (!state.isActive) {
        return null;
    }

    return children;
}

NavigationPage.propTypes = {
    id: PropTypes.string.isRequired,
    title: MessagePropType,

    route: PropTypes.string.isRequired,
    routeParams: PropTypes.object,

    onActivate: PropTypes.func,

    children: PropTypes.node.isRequired,
};

NavigationPage.defaultProps = {
    routeParams: undefined,
    onActivate: undefined,
    title: undefined,
};


function useNavigationPageState({ id, title, route, routeParams, onActivate }) {
    const intl = useIntl();
    const navigation = useNavigation();

    if (!navigation) {
        throw new Error('Cannot use NavigationPage without Navigation context');
    }

    const [ state ] = useState(() => observable({
        // Assumption: id & route will not change during the component's lifecycle
        id,
        route,

        get isActive() {
            return navigation.activePageId === state.id;
        },
    }));

    // Add state to Navigation context on mount
    useEffect(() => {
        navigation.addPage(state);

        return () => navigation.removePage(state.id);
    }, [ navigation, state ]);
    
    if (!title && process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(`NavigationPage "${id}" has no title`);
    }

    // When we're active, update the document title
    useEffect(() => {
        if (state.isActive && title) {
            document.title = messageToString(title, intl);
        }
    }, [ state.isActive, title ]);
    
    // When we become active, we want to set the URL if
    //  routing is enabled and fire a callback to notify the
    //  consumer of the change
    useEffect(() => {
        return reaction(
            () => ({
                isActive: state.isActive,
                routeParams: navigation.routeParams ?? routeParams,
            }),
            ({ isActive, routeParams }) => {
                if (isActive) {
                    if (navigation.isRoutingEnabled) {
                        updateURL(navigation.baseRoute, route, routeParams);
                        onActivate?.(routeParams, state);
                    }
                }
            },
            {
                name: 'NavigationPage.isActiveReaction',
                equals: comparer.structural,
            },
        );
    }, [ state, navigation, navigation.isRoutingEnabled, onActivate, routeParams ]);

    return state;
}


function updateURL(baseRoute, route, routeParams) {
    if (typeof window === 'undefined') {
        return;
    }

    const queryParams = window.location.search;
    const currentPath = `${window.location.pathname}${queryParams}`;

    let updatedPath = `${baseRoute}${route}`;

    // If we're given route params, we should replace it in the route
    if (typeof routeParams === 'object') {
        Object.entries(routeParams).forEach(([ key, value ]) => {
            const paramRegex = new RegExp(`:(${key})`);
            updatedPath = updatedPath.replace(paramRegex, value);
        });
    }

    updatedPath += `${queryParams}`;

    if (currentPath !== updatedPath) {
        window.history.pushState(null, '', updatedPath);
    }
}


export default observer(NavigationPage);